import React, { useState, useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Box,
  Divider,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Dashboard as DashboardIcon,
  Devices as DevicesIcon,
  Timeline as TimelineIcon,
  Group as GroupIcon,
  BusinessCenter as BusinessCenterIcon,
  Description as LicenseIcon,
  Logout as LogoutIcon,
  Security as SecurityIcon, // New icon for "Disk Encryption"
} from "@mui/icons-material";
import AuthContext from "../../AuthContext";
import Logo from "../../assets/logo.png";

const drawerWidth = 260;
const collapsedWidth = 70;

const Sidebar = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const auth = useContext(AuthContext);
  const { sidebarCollapsed, setSidebarCollapsed } = useContext(AuthContext);

  const [mobileOpen, setMobileOpen] = useState(false);
  const toggleCollapse = () => setSidebarCollapsed(!sidebarCollapsed);
  const toggleMobile = () => setMobileOpen(!mobileOpen);

  const menuItems = [
    { name: "Dashboard", path: "/", icon: <DashboardIcon /> },
    { name: "Devices", path: "/devices", icon: <DevicesIcon /> },
    { name: "Activity Log", path: "/activity-log", icon: <TimelineIcon /> },
    { name: "Users", path: "/users", icon: <GroupIcon /> },
    ...(auth.userType === "superadmin"
      ? [{ name: "Companies", path: "/companies", icon: <BusinessCenterIcon /> }]
      : []),
    ...(auth.userType === "admin"
      ? [{ name: "License", path: "/license", icon: <LicenseIcon /> }]
      : []),
    { name: "Departments", path: "/departments", icon: <BusinessCenterIcon /> },
  ];

  const drawerBg = isDarkMode ? theme.palette.background.default : "#fff";
  const textColor = theme.palette.text.primary;
  const iconColor = theme.palette.text.secondary;
  const hoverBg = isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)";
  const selectedBg = theme.palette.action.selected;
  const logoutHover = theme.palette.error.light;

  const renderMenuItem = (item) => {
    const selected = location.pathname === item.path;
    return (
      <Tooltip key={item.name} title={sidebarCollapsed ? item.name : ""} placement="right" arrow>
        <ListItemButton
          component={Link}
          to={item.path}
          selected={selected}
          sx={{
            mb: 0.5,
            borderRadius: 2,
            color: selected ? textColor : iconColor,
            bgcolor: selected ? selectedBg : "transparent",
            transition: "all 0.2s ease",
            "&:hover": { bgcolor: hoverBg, transform: "translateX(4px)" },
            py: 1.5,
          }}
        >
          <ListItemIcon
            sx={{
              color: selected ? textColor : iconColor,
              minWidth: 0,
              justifyContent: "center",
              mr: !sidebarCollapsed ? 2 : 0,
            }}
          >
            {item.icon}
          </ListItemIcon>
          {!sidebarCollapsed && (
            <ListItemText
              primary={item.name}
              sx={{
                my: 0,
                "& .MuiListItemText-primary": {
                  fontWeight: selected ? 600 : 500,
                  fontSize: "0.95rem",
                },
              }}
            />
          )}
        </ListItemButton>
      </Tooltip>
    );
  };

  const renderLogout = () => (
    <Tooltip title={sidebarCollapsed ? "Logout" : ""} placement="right" arrow>
      <ListItemButton
        onClick={auth.signout}
        sx={{
          mb: 2,
          borderRadius: 2,
          color: textColor,
          "&:hover": { bgcolor: logoutHover + "2", transform: "translateX(4px)" },
          py: 1.5,
        }}
      >
        <ListItemIcon
          sx={{
            color: logoutHover,
            minWidth: 0,
            justifyContent: "center",
            mr: !sidebarCollapsed ? 2 : 0,
          }}
        >
          <LogoutIcon />
        </ListItemIcon>
        {!sidebarCollapsed && (
          <ListItemText
            primary="Logout"
            sx={{
              my: 0,
              "& .MuiListItemText-primary": {
                fontWeight: 500,
                color: logoutHover,
                fontSize: "0.95rem",
              },
            }}
          />
        )}
      </ListItemButton>
    </Tooltip>
  );

  return (
    <>
      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        open={!sidebarCollapsed}
        sx={{
          width: sidebarCollapsed ? collapsedWidth : drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: sidebarCollapsed ? collapsedWidth : drawerWidth,
            boxSizing: "border-box",
            background: drawerBg,
            color: textColor,
            borderRight: "none",
            boxShadow: isDarkMode ? "4px 0 12px rgba(0,0,0,0.3)" : "4px 0 12px rgba(0,0,0,0.08)",
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            height: "100vh",
            overflow: "hidden",
          },
        }}
      >
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" p={2} borderBottom={`1px solid ${isDarkMode ? "#4A5568" : "#E2E8F0"}`}>
          {!sidebarCollapsed ? (
            <Box display="flex" alignItems="center" gap={1} sx={{ 
              animation: "fadeInScale 0.5s ease-in-out forwards",
              opacity: 0,
              transform: "scale(0.9)",
              "&:hover": { transform: "scale(1.05)" }
            }}>
              <SecurityIcon color="primary" />
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                KosmicWipe
              </Typography>
            </Box>
          ) : null}
          <IconButton onClick={toggleCollapse} sx={{ color: iconColor }}>
            {sidebarCollapsed ? <MenuIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Box>

        {/* Menu */}
        <List sx={{ p: 1, mt: 2 }}>{menuItems.map(renderMenuItem)}</List>
        <Divider sx={{ my: 2, bgcolor: theme.palette.divider }} />

        {renderLogout()}

        {/* Footer Placeholder */}
        <Box sx={{ mt: "auto", mb: 2, textAlign: "center" }}>
          {!sidebarCollapsed && (
            <Typography variant="caption" color={iconColor}>
              Secure Data Solutions
            </Typography>
          )}
        </Box>
      </Drawer>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={toggleMobile}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", lg: "none" },
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            background: drawerBg,
            color: textColor,
            borderRadius: 0,
            height: "100vh",
          },
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" p={2} borderBottom={`1px solid ${isDarkMode ? "#4A5568" : "#E2E8F0"}`}>
          <Box display="flex" alignItems="center" gap={1} sx={{ 
            animation: "fadeInScale 0.5s ease-in-out forwards",
            opacity: 0,
            transform: "scale(0.9)",
            "&:hover": { transform: "scale(1.05)" }
          }}>
            <SecurityIcon color="primary" />
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              KosmicWipe
            </Typography>
          </Box>
          <IconButton onClick={toggleMobile} sx={{ color: iconColor }}>
            <ChevronLeftIcon />
          </IconButton>
        </Box>

        <List sx={{ p: 1 }}>
          {menuItems.map((item) => (
            <ListItemButton
              key={item.name}
              component={Link}
              to={item.path}
              selected={location.pathname === item.path}
              onClick={() => setMobileOpen(false)}
              sx={{
                mb: 1,
                borderRadius: 2,
                color: textColor,
                "&:hover": { bgcolor: hoverBg, transform: "translateX(8px)" },
                py: 2,
              }}
            >
              <ListItemIcon sx={{ color: iconColor }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.name}
                sx={{ "& .MuiListItemText-primary": { fontWeight: 500, fontSize: "1rem" } }}
              />
            </ListItemButton>
          ))}
          <Divider sx={{ my: 2, bgcolor: isDarkMode ? "#4A5568" : "#E2E8F0" }} />
          {renderLogout()}
        </List>
      </Drawer>

      {/* CSS Animation */}
      <style>
        {`
          @keyframes fadeInScale {
            from {
              opacity: 0;
              transform: scale(0.9);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
        `}
      </style>
    </>
  );
};

export default Sidebar;